import React, { useState } from 'react';
import { Box, IconButton, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useTranslation } from 'react-i18next';
import { DiscountModeEnum } from '../../common/enums/invoicing.enum';
import { DEFAULT_CURRENCY, formatMoney } from '../../utilities/helper/money.helper';
import { DiscountProblem } from '../../utilities/helper/discount.helper';
import SegmentedControl from '../SegmentedControl';
import { LINE_COLUMNS, lineFieldSx } from './LineTable';

/** A discount as an editor holds it: the value is text until it is saved. */
export interface DiscountState {
  /** The editor's own; never sent. */
  key: string;
  label: string;
  mode: DiscountModeEnum;
  valueText: string;
  /** The key of the line it lowers: a billing item id, or an invoice editor's line key. */
  appliesTo: string;
}

let nextKey = 0;
export const newDiscountKey = () => `discount-${++nextKey}`;

export const newDiscount = (appliesTo: string): DiscountState => ({
  key: newDiscountKey(),
  label: '',
  mode: DiscountModeEnum.PERCENT,
  valueText: '',
  appliesTo
});

interface DiscountRowProps {
  discount: DiscountState;
  onChange: (patch: Partial<DiscountState>) => void;
  onRemove: () => void;
  /** The lines it can lower, in the editor's order, discounts left out. */
  targets: { key: string; name: string }[];
  /** The lowered line: its net for a monthly line, or null when it is counted per day. */
  targetNet: number | null;
  targetName: string;
  /** What it takes off, already capped; null when the line's amount is not known yet. */
  amount: number | null;
  problem?: DiscountProblem;
  /** After a save attempt every problem shows, touched or not. */
  showErrors?: boolean;
  labelRef?: (element: HTMLInputElement | null) => void;
}

const ErrorLine: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography id={id} sx={{ fontSize: 12, color: 'error.main', mt: 0.5, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
    <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14, mt: '1px' }} />
    <span>{children}</span>
  </Typography>
);

/**
 * A discount, as a row right under the line it lowers: what it is called, what it lowers,
 * how much, and the amount it takes off in green with a minus.
 *
 * It is a line of its own on the invoice, not a field of the lowered line, so the parent
 * reads "Popust za drugo dete -900,00" under "Boravak 9.000,00" rather than a price that is
 * simply lower than last month's with nothing saying why.
 */
const DiscountRow: React.FC<DiscountRowProps> = ({
  discount,
  onChange,
  onRemove,
  targets,
  targetNet,
  targetName,
  amount,
  problem,
  showErrors,
  labelRef
}) => {
  const { t } = useTranslation();
  const [touched, setTouched] = useState({ label: false, value: false });
  const id = `discount-${discount.key}`;
  const labelError = (touched.label || showErrors) && problem?.label;
  const valueError = (touched.value || showErrors) && problem?.value;
  const isPercent = discount.mode === DiscountModeEnum.PERCENT;
  const perDay = targetNet === null;
  const name = discount.label.trim() || t('discounts.unnamed');

  const base = perDay
    ? isPercent
      ? t('discounts.ofPerDay', { name: targetName.toLowerCase() })
      : t('discounts.atMostPerDay', { name: targetName.toLowerCase() })
    : t('discounts.of', { amount: formatMoney(targetNet) });

  return (
    <Box
      component="li"
      aria-label={t('discounts.rowLabel', { name, target: targetName })}
      sx={{
        display: 'grid',
        gridTemplateColumns: LINE_COLUMNS,
        columnGap: 1.5,
        alignItems: 'start',
        p: '12px 16px',
        bgcolor: 'surface.subtle',
        borderTop: 1,
        borderTopStyle: 'dashed',
        borderColor: 'line'
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
        <SubdirectoryArrowRightIcon aria-hidden sx={{ fontSize: 20, color: 'success.main', mt: 1.5, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <TextField
            fullWidth
            value={discount.label}
            placeholder={t('discounts.labelPlaceholder')}
            error={!!labelError}
            inputRef={labelRef}
            onChange={event => onChange({ label: event.target.value })}
            onBlur={() => setTouched(prev => ({ ...prev, label: true }))}
            slotProps={{
              htmlInput: {
                'aria-label': t('discounts.labelAria'),
                'aria-invalid': !!labelError,
                'aria-describedby': labelError ? `${id}-label-error` : undefined
              }
            }}
            sx={lineFieldSx}
          />
          {labelError && <ErrorLine id={`${id}-label-error`}>{labelError}</ErrorLine>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
            <Typography id={`${id}-target-label`} sx={{ fontSize: 12, color: 'text.secondary' }}>
              {t('discounts.lowers')}
            </Typography>
            <Select
              size="small"
              value={discount.appliesTo}
              labelId={`${id}-target-label`}
              onChange={event => onChange({ appliesTo: event.target.value })}
              sx={{ height: 32, fontSize: 13, borderRadius: '8px', minWidth: 0, maxWidth: '100%', bgcolor: 'surface.main' }}
            >
              {targets.map(target => (
                <MenuItem key={target.key} value={target.key} sx={{ fontSize: 13 }}>
                  {target.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Box>

      <Box>
        <TextField
          fullWidth
          value={discount.valueText}
          error={!!valueError}
          onChange={event => {
            if (/^[\d.,\s]*$/.test(event.target.value)) onChange({ valueText: event.target.value });
          }}
          onBlur={() => setTouched(prev => ({ ...prev, value: true }))}
          slotProps={{
            htmlInput: {
              inputMode: 'decimal',
              'aria-label': t('discounts.valueAria', { name }),
              'aria-invalid': !!valueError,
              'aria-describedby': `${id}-value-message`,
              style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
            },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{isPercent ? '%' : DEFAULT_CURRENCY}</Typography>
                </InputAdornment>
              )
            }
          }}
          sx={lineFieldSx}
        />
        {valueError ? (
          <ErrorLine id={`${id}-value-message`}>{valueError}</ErrorLine>
        ) : (
          <Typography id={`${id}-value-message`} sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
            {base}
          </Typography>
        )}
      </Box>

      <SegmentedControl
        ariaLabel={t('discounts.modeAria', { name })}
        value={discount.mode}
        onChange={mode => onChange({ mode })}
        options={[
          { value: DiscountModeEnum.PERCENT, label: '%' },
          { value: DiscountModeEnum.AMOUNT, label: DEFAULT_CURRENCY }
        ]}
      />

      <Typography
        sx={{ fontSize: 15, fontWeight: 600, color: 'success.main', textAlign: 'right', fontVariantNumeric: 'tabular-nums', lineHeight: '44px', whiteSpace: 'nowrap' }}
      >
        {amount !== null
          ? `−${formatMoney(amount)}`
          : isPercent
            ? `−${discount.valueText.trim() || 0} %`
            : `−${discount.valueText.trim() || formatMoney(0)}`}
      </Typography>

      <IconButton aria-label={t('discounts.remove', { name })} onClick={onRemove} sx={{ width: 36, height: 36, borderRadius: '10px', mt: 0.5 }}>
        <DeleteOutlineIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
      </IconButton>
    </Box>
  );
};

export default DiscountRow;
